/* ═══════════════════════════════════════════════════════════════
   ModuleTagsManager — "Manage tags" dialog opened from the pencil on the
   Tags rail header (modules.edit). One standard DataTable of every tag in
   use (Tag · Modules using it) with Rename / Merge into… / Delete per row.
   The firm's tag catalog (`module_tags`, migration 060) makes a tag exist
   before any module uses it, so the dialog opens with a New tag row. Rename /
   merge / delete rewrite the tag array of the affected modules AND the
   catalog (`ModuleService.renameTag/mergeTag/deleteTag`), after a confirm
   that names the count, and end in ONE toast with the result.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { DataTable, type Column } from './DataTable';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ConfirmDialog } from './ui/confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { SearchableSelect } from './SearchableSelect';
import { ModuleService, type TagBulkResult } from '../services/moduleService';
import { useTableSync } from '../hooks/useTableSync';
import { buildTagRows, type TagRow } from '../../shared/module-tags';
import { MAX_TAG_LEN } from '../../shared/module-categories';
import { hasPermission } from '../../shared/permissions';

type Action = { kind: 'rename' | 'merge' | 'delete'; tag: TagRow } | null;

const modulesWord = (n: number) => `${n} module${n === 1 ? '' : 's'}`;

export interface ModuleTagsManagerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userName: string;
  userPermissions: string[];
  toast: (msg: string, isError?: boolean) => void;
}

export function ModuleTagsManager({
  open,
  onOpenChange,
  userName,
  userPermissions,
  toast,
}: ModuleTagsManagerProps) {
  const [tags, setTags] = React.useState<TagRow[]>([]);
  const [newTag, setNewTag] = React.useState('');
  const [newTagError, setNewTagError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [action, setAction] = React.useState<Action>(null);
  const [text, setText] = React.useState('');
  const [mergeInto, setMergeInto] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      // Archived modules carry tags too — rewrite and count across all of them.
      // The catalog may be unavailable on an older server: the table then
      // shows the tags in use, never blank.
      const [catalog, modules] = await Promise.allSettled([
        ModuleService.listTags(),
        ModuleService.listAll(),
      ]);
      if (modules.status === 'rejected') throw modules.reason;
      setTags(buildTagRows(catalog.status === 'fulfilled' ? catalog.value : [], modules.value));
    } catch {
      toast('Could not load the tags', true);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  React.useEffect(() => {
    if (open) void load();
  }, [open, load]);
  useTableSync(open ? ['module_library', 'module_tags'] : null, () => void load());

  const canEdit = hasPermission(userPermissions, 'modules.edit');

  const addTag = async () => {
    const name = newTag.trim();
    if (!name) {
      setNewTagError('Enter a tag name.');
      return;
    }
    if (tags.some((t) => t.name.toLowerCase() === name.toLowerCase())) {
      setNewTagError(`“${name}” already exists.`);
      return;
    }
    setBusy(true);
    try {
      await ModuleService.createTag(name, userName);
      setNewTag('');
      setNewTagError(null);
      toast(`Added tag “${name}”`);
      await load();
    } catch (err) {
      setNewTagError(err instanceof Error ? err.message : 'The tag could not be added.');
    } finally {
      setBusy(false);
    }
  };

  const begin = (kind: 'rename' | 'merge' | 'delete', tag: TagRow) => {
    setAction({ kind, tag });
    setText(kind === 'rename' ? tag.name : '');
    setMergeInto('');
  };
  const close = () => {
    if (!busy) setAction(null);
  };

  const run = async () => {
    if (!action) return;
    const { kind, tag } = action;
    setBusy(true);
    try {
      let result: TagBulkResult;
      let done: string;
      if (kind === 'rename') {
        result = await ModuleService.renameTag(tag.name, text.trim(), userName);
        done = `Renamed “${tag.name}” to “${text.trim()}” on ${modulesWord(result.updated)}`;
      } else if (kind === 'merge') {
        result = await ModuleService.mergeTag(tag.name, mergeInto, userName);
        done = `Merged “${tag.name}” into “${mergeInto}” on ${modulesWord(result.updated)}`;
      } else {
        result = await ModuleService.deleteTag(tag.name, userName);
        done = `Deleted “${tag.name}” from ${modulesWord(result.updated)}`;
      }
      if (result.failed.length) {
        toast(
          `${done}. Could not update ${modulesWord(result.failed.length)}: ${result.failed
            .map((f) => f.name || f.id)
            .join(', ')}`,
          true
        );
      } else {
        toast(done);
      }
      setAction(null);
      await load();
    } catch {
      toast('Could not update the tags', true);
    } finally {
      setBusy(false);
    }
  };

  const columns: Column<TagRow>[] = [
    { key: 'name', label: 'Tag', width: 240, getValue: (t) => t.name, filterType: 'none' },
    {
      key: 'count',
      label: 'Modules using it',
      width: 150,
      getValue: (t) => t.count,
      filterType: 'none',
    },
    {
      key: 'actions',
      label: 'Actions',
      width: 280,
      sortable: false,
      filterType: 'none',
      render: (t) =>
        canEdit ? (
          <span style={{ display: 'flex', gap: 'var(--space-1)' }}>
            <Button variant="ghost" size="sm" onClick={() => begin('rename', t)}>
              Rename
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={tags.length < 2}
              onClick={() => begin('merge', t)}
            >
              Merge into…
            </Button>
            <Button variant="ghost" size="sm" onClick={() => begin('delete', t)}>
              Delete
            </Button>
          </span>
        ) : null,
    },
  ];

  const renameClash =
    action?.kind === 'rename' &&
    text.trim().toLowerCase() !== action.tag.name.toLowerCase() &&
    tags.some((t) => t.name.toLowerCase() === text.trim().toLowerCase());
  const renameValid =
    action?.kind === 'rename' &&
    text.trim().length > 0 &&
    text.trim().length <= MAX_TAG_LEN &&
    text.trim() !== action.tag.name;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Manage tags</DialogTitle>
            <DialogDescription>
              Add a tag, or rename, merge or delete one across every module that uses it.
            </DialogDescription>
          </DialogHeader>
          {canEdit && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
              <Label htmlFor="sf-new-tag">New tag</Label>
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <Input
                  id="sf-new-tag"
                  value={newTag}
                  maxLength={MAX_TAG_LEN}
                  aria-invalid={newTagError ? true : undefined}
                  aria-describedby={newTagError ? 'sf-new-tag-error' : undefined}
                  disabled={busy}
                  onChange={(e) => {
                    setNewTag(e.target.value);
                    setNewTagError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !busy) void addTag();
                  }}
                />
                <Button onClick={() => void addTag()} disabled={busy}>
                  Add
                </Button>
              </div>
              {newTagError && (
                <p
                  id="sf-new-tag-error"
                  role="alert"
                  style={{
                    color: 'var(--color-danger)',
                    fontSize: 'var(--font-size-sm)',
                    margin: 0,
                  }}
                >
                  {newTagError}
                </p>
              )}
            </div>
          )}
          <div
            style={{
              height: '50vh',
              minHeight: 0,
              minWidth: 0,
              width: '100%',
              overflowX: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <DataTable<TagRow>
              columns={columns}
              data={tags}
              keyField="name"
              searchPlaceholder="Search tags…"
              initialSort={{ key: 'name', direction: 'asc' }}
              storageKey="module-tags-manager.v1"
              compactToolbar
              permissions={userPermissions}
              emptyTitle={loading ? 'Loading tags…' : 'No tags yet'}
              emptyMessage={loading ? undefined : 'Add a tag above and it will show up here.'}
            />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={action?.kind === 'rename'} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Rename tag</DialogTitle>
            <DialogDescription>
              “{action?.tag.name}” changes on {modulesWord(action?.tag.count ?? 0)}.
              {renameClash ? ' That name exists already, so the two tags will merge.' : ''}
            </DialogDescription>
          </DialogHeader>
          <Input
            aria-label="New tag name"
            value={text}
            maxLength={MAX_TAG_LEN}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && renameValid && !busy) void run();
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={() => void run()} disabled={!renameValid || busy}>
              {busy ? 'Working…' : 'Rename'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={action?.kind === 'merge'} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Merge tag</DialogTitle>
            <DialogDescription>
              “{action?.tag.name}” on {modulesWord(action?.tag.count ?? 0)} becomes the tag you
              pick.
            </DialogDescription>
          </DialogHeader>
          <SearchableSelect
            ariaLabel="Merge into tag"
            value={mergeInto}
            onChange={setMergeInto}
            placeholder="Merge into…"
            searchPlaceholder="Search tags…"
            clearable={false}
            options={tags
              .filter((t) => t.name !== action?.tag.name)
              .map((t) => ({ value: t.name, label: t.name, count: t.count }))}
          />
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={() => void run()} disabled={!mergeInto || busy}>
              {busy ? 'Working…' : 'Merge'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={action?.kind === 'delete'}
        onOpenChange={(o) => {
          if (!o) close();
        }}
        title={`Delete tag “${action?.tag.name ?? ''}”?`}
        description={`Delete “${action?.tag.name ?? ''}”? It will be removed from ${modulesWord(
          action?.tag.count ?? 0
        )}. Their other tags stay.`}
        confirmLabel="Delete tag"
        busy={busy}
        onConfirm={() => void run()}
      />
    </>
  );
}

export default ModuleTagsManager;
